import type {
  AlterarSenha,
  ConfiguracaoTotp,
  LoginEquipe,
  RespostaLoginEquipe,
  SessaoEquipe,
  UsuarioAtual,
} from '@mymaxx/shared';
import { lerAccessToken, useSessao } from './sessao';

/**
 * Cliente HTTP do painel. Em dev, o Vite faz proxy de /v1 para a API; em produção,
 * VITE_API_URL aponta para https://api.mymaxx.maxxconectado.online.
 * Em 401 numa rota autenticada, tenta UM refresh (cookie httpOnly) e repete a chamada.
 */
const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';

export class ErroApi extends Error {
  constructor(
    public readonly status: number,
    public readonly corpo: {
      message?: string;
      erros?: { campo: string; mensagem: string }[];
    } | null,
  ) {
    super(corpo?.message ?? `Erro ${status}`);
  }
  /** Mensagem amigável para exibir no formulário. */
  get mensagem(): string {
    if (this.corpo?.erros?.length) return this.corpo.erros.map((e) => e.mensagem).join('. ');
    if (typeof this.corpo?.message === 'string') return this.corpo.message;
    return this.status === 0 ? 'Não foi possível falar com o servidor' : `Erro ${this.status}`;
  }
}

export interface Opcoes extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** false para rotas públicas (login) — não anexa token nem tenta refresh. */
  autenticado?: boolean;
}

let refreshEmAndamento: Promise<boolean> | null = null;

/** Pede um access token novo usando o cookie de refresh. Deduplica chamadas concorrentes. */
export async function tentarRefresh(): Promise<boolean> {
  refreshEmAndamento ??= (async () => {
    try {
      const r = await fetch(`${BASE_URL}/v1/auth/equipe/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!r.ok) return false;
      useSessao.getState().definir((await r.json()) as SessaoEquipe);
      return true;
    } catch {
      return false;
    } finally {
      refreshEmAndamento = null;
    }
  })();
  return refreshEmAndamento;
}

async function executar<T>(caminho: string, opcoes: Opcoes, tentarNovamente: boolean): Promise<T> {
  const { body, autenticado = true, headers, ...init } = opcoes;
  const token = autenticado ? lerAccessToken() : null;
  let resposta: Response;
  try {
    resposta = await fetch(`${BASE_URL}${caminho}`, {
      ...init,
      credentials: 'include',
      headers: {
        // Arquivo (a planilha da importação) vai cru: transformá-lo em JSON o corromperia,
        // e o servidor lê essa rota como corpo binário.
        ...(body !== undefined && !(body instanceof Blob)
          ? { 'Content-Type': 'application/json' }
          : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(headers ?? {}),
      },
      body: body instanceof Blob ? body : body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ErroApi(0, null);
  }

  if (resposta.status === 401 && autenticado && tentarNovamente && (await tentarRefresh())) {
    return executar<T>(caminho, opcoes, false);
  }
  if (resposta.status === 401 && autenticado) useSessao.getState().limpar();

  const corpo = resposta.status === 204 ? null : await resposta.json().catch(() => null);
  if (!resposta.ok) throw new ErroApi(resposta.status, corpo);
  return corpo as T;
}

/**
 * Baixa um arquivo autenticado. Precisa de fetch com Bearer: um `<a href>` não leva token,
 * e a rota não é pública.
 */
export async function baixarArquivo(caminho: string, nomePadrao: string): Promise<void> {
  const resposta = await fetch(`${BASE_URL}${caminho}`, {
    headers: { Authorization: `Bearer ${lerAccessToken() ?? ''}` },
    credentials: 'include',
  });
  if (!resposta.ok) throw new Error('Não consegui gerar o arquivo.');

  const nomeDoServidor = /filename="([^"]+)"/.exec(
    resposta.headers.get('content-disposition') ?? '',
  )?.[1];
  const url = URL.createObjectURL(await resposta.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = nomeDoServidor ?? nomePadrao;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export const api = {
  get: <T>(caminho: string, opcoes: Opcoes = {}) =>
    executar<T>(caminho, { ...opcoes, method: 'GET' }, true),
  post: <T>(caminho: string, body?: unknown, opcoes: Opcoes = {}) =>
    executar<T>(caminho, { ...opcoes, method: 'POST', body }, true),
  put: <T>(caminho: string, body?: unknown, opcoes: Opcoes = {}) =>
    executar<T>(caminho, { ...opcoes, method: 'PUT', body }, true),
  patch: <T>(caminho: string, body?: unknown, opcoes: Opcoes = {}) =>
    executar<T>(caminho, { ...opcoes, method: 'PATCH', body }, true),
  delete: <T>(caminho: string, opcoes: Opcoes = {}) =>
    executar<T>(caminho, { ...opcoes, method: 'DELETE' }, true),
};

export const authApi = {
  login: (dados: LoginEquipe) =>
    api.post<RespostaLoginEquipe>('/v1/auth/equipe/login', dados, { autenticado: false }),
  validarTotp: (tokenTemporario: string, codigo: string) =>
    api.post<SessaoEquipe>(
      '/v1/auth/equipe/totp',
      { tokenTemporario, codigo },
      { autenticado: false },
    ),
  logout: () => api.post<void>('/v1/auth/equipe/logout', undefined, { autenticado: false }),
  logoutTodos: () => api.post<{ sessoesRevogadas: number }>('/v1/auth/equipe/logout-todos'),
  eu: () => api.get<UsuarioAtual>('/v1/auth/equipe/eu'),
  configurarTotp: () => api.post<ConfiguracaoTotp>('/v1/auth/equipe/totp/configurar'),
  ativarTotp: (codigo: string) => api.post<SessaoEquipe>('/v1/auth/equipe/totp/ativar', { codigo }),
  alterarSenha: (dados: AlterarSenha) => api.post<SessaoEquipe>('/v1/auth/equipe/senha', dados),

  /** Foto de perfil: pede a URL, envia direto ao storage e confirma. */
  async trocarFoto(arquivo: File): Promise<UsuarioAtual> {
    const destino = await api.post<{ chave: string; url: string }>(
      '/v1/auth/equipe/avatar/preparar',
      { mimeType: arquivo.type, tamanho: arquivo.size },
    );
    const envio = await fetch(destino.url, {
      method: 'PUT',
      body: arquivo,
      headers: { 'Content-Type': arquivo.type },
    });
    if (!envio.ok) throw new Error('Não consegui enviar a foto.');
    return api.post<UsuarioAtual>('/v1/auth/equipe/avatar', { chave: destino.chave });
  },
  removerFoto: () => api.delete<UsuarioAtual>('/v1/auth/equipe/avatar'),
};

export interface SaudeApi {
  status: 'ok' | 'degradado';
  banco: { ok: boolean; ms: number; erro?: string };
  redis: { ok: boolean; ms: number; erro?: string };
  storage: { ok: boolean; ms: number; erro?: string };
  hubsoft: { ok: boolean; ms: number; erro?: string };
}
